import { ErrorNotification } from '@gravitate-js/excalibrr'
import { Col, Form, Row } from 'antd'
import { useMemo, useState } from 'react'

import { MetricDetail } from '../MetricDetail'
import { ShiftHeaderColumn } from './ShiftHeaderColumn'
import { usePayroll } from '@/api/usePayroll'
import dayjs from 'dayjs'
import { PayPeriodShiftPay, ShiftPayrollFormType } from '../types'

type ShiftFormProps = {
  shiftDate: string
  shiftPay: PayPeriodShiftPay
  driver_id: number
}

export function ShiftForm({ shiftDate, shiftPay, driver_id }: ShiftFormProps) {
  const [editing, setEditing] = useState(false)
  const [form] = Form.useForm<ShiftPayrollFormType>()
  const { overrideShiftPayDetailMutation } = usePayroll()
  const { mutateAsync: overrideShift } = overrideShiftPayDetailMutation()

  const calculateShiftTimes = () => {
    const shiftPayStart = shiftPay.override_start || shiftPay.start
    const shiftPayEnd = shiftPay.override_end || shiftPay.end

    if (shiftPayStart && shiftPayEnd) {
      return [dayjs(shiftPayStart), dayjs(shiftPayEnd)]
    }
    if (shiftPayStart && !shiftPayEnd) {
      return [dayjs(shiftPayStart), dayjs(shiftPayStart).add(12, 'hours')]
    }
    if (!shiftPayStart && shiftPayEnd) {
      return [dayjs(shiftPayEnd).subtract(12, 'hours'), dayjs(shiftPayEnd)]
    }
    // When neither date exists on the response, we'll use the shift pay date as a fallback for the initial
    // range picker values.
    const start = dayjs(shiftDate).startOf('day')
    const end = dayjs(shiftDate).startOf('day').add(12, 'hours')

    return [start, end]
  }

  const initialValues = useMemo(() => {
    const miles = shiftPay.pay_items.find((item) => item.name === 'Total Miles')
    return {
      date: shiftPay.start,
      driver_id,
      shiftTimes: calculateShiftTimes(),
      'Total Miles': miles?.override_quantity ?? miles?.quantity ?? 0,
      override_reason: shiftPay.override_reason,
    }
  }, [shiftPay, driver_id])

  async function handleSave(formData: ShiftPayrollFormType) {
    const saveRequest = {
      date: dayjs(shiftDate)?.toISOString(),
      driver_id,
      shift_id: shiftPay.shift_id,
      shift_start: formData.shiftTimes[0]?.toISOString(),
      shift_end: formData.shiftTimes[1]?.toISOString(),
      miles: formData['Total Miles'],
      reason: formData.override_reason,
    }
    await overrideShift(saveRequest)
    resetForm()
  }

  function resetForm() {
    form.setFieldsValue(initialValues)
  }

  return (
    <Form
      id='shift-form'
      form={form}
      onFinish={handleSave}
      onReset={resetForm}
      onFinishFailed={ErrorNotification}
      initialValues={initialValues}
    >
      <Row className='pay-row'>
        <Col span={8} className='pay-summary bg-2 vertical-flex-center'>
          <ShiftHeaderColumn shiftPay={shiftPay} editing={editing} setEditing={setEditing} form={form} />
        </Col>
        <Col span={16}>
          <Row>
            {shiftPay.pay_items.map((detail, index) => (
              <MetricDetail
                key={index}
                detail={detail}
                count={shiftPay.pay_items.length}
                index={index}
                editing={editing}
              />
            ))}
          </Row>
        </Col>
      </Row>
    </Form>
  )
}
